import endpoints from "../../utils/endpoints";
import { publicAxios as axios } from "../../config/axios";
import globalConstant from "../../utils/constants";
import {
  OrderHandlerAllocationType,
  OrderStatus,
  OrderTypes,
  TransactionType,
  EventBusTypes,
  EventBusEventNames,
} from "./enums";
import { CollectPaymentPayload, PaymentLink } from "./types";
import {
  OrderItemActionType,
  OrderItemActionResponse,
} from "./types/orderItemsActionTypes";
import {
  CreateManualDeliveryOptionPayload,
  ConfirmEstimateSplitPayload,
  ManualDeliveryOptionResponse,
} from "./types/estimateConfirmationTypes";
import type { EstimateSplitMode } from "./services/EstimateAllocatorService/types";

class Services {
  async placeGaraazOrder(payload: any, query?: string) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.placeGaraazOrder + (query ? `?${query}` : ""),
      data: payload,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }
  async getAllGaraazOrders(
    filterString: string,
    familySort: "priority" | "classic" = "priority",
  ) {
    const applyVirtualType = filterString.includes("orderType=REGULAR");
    if (applyVirtualType) {
      filterString += "&orderType=VIRTUAL";
    }
    const response = await axios({
      method: globalConstant.GET,
      url:
        endpoints.getAllGaraazOrdersV3 +
        filterString +
        `&familySort=${familySort}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    return response.data;
  }

  async getAllGaraazOrdersV2(filterString: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getAllGaraazOrders + filterString,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    return response.data;
  }

  async getPartDetailsByPartId(partId: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `/v1/part-info/${partId}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    return response.data;
  }

  async setOrderRemarks(orderId: string, orderPayload: any) {
    try {
      const response = await axios({
        method: globalConstant.POST,
        url: endpoints.setOrderRemarks(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data: orderPayload,
      });
      return response.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  async setOrderETA(orderId: string, orderETAPayload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.setOrderETA(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: orderETAPayload,
    });
    return response.data;
  }

  async createPaymentLink(
    orderId: string,
    payload: {
      transactionType: TransactionType;
      amount: number;
      notes?: string;
    },
  ): Promise<PaymentLink> {
    const response = await axios<PaymentLink>({
      method: globalConstant.POST,
      url: endpoints.createPaymentLink(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async updateDeliveryOption(
    estimateNo: string,
    splitId: string,
    optionId: string,
    payload: any
  ) {
    const response = await axios({
      method: globalConstant.PATCH,
      url: `/v1/estimate/${estimateNo}/splits/${splitId}/delivery-options/${optionId}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async patchEstimateSplit(
    estimateNo: string,
    splitId: string,
    payload: {
      transactionType?: string;
      discountPercentage?: number;
      creditNotePercentage?: number;
      fulfillmentStatus?: string;
      payableBy?: string;
      payableTo?: string;
      status?: string;
      buyTime?: number;
    },
  ) {
    const response = await axios({
      method: globalConstant.PATCH,
      url: endpoints.patchEstimateSplit(estimateNo, splitId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async syncEstimateSplitsItems(
    estimateNo: string,
    payload: {
      splits: Array<{
        splitId: string;
        items: Array<{ partId: string; qty: number }>;
      }>;
    },
  ) {
    const response = await axios({
      method: globalConstant.PATCH,
      url: endpoints.syncEstimateSplitsItems(estimateNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async getEstimates(orderNo: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `/v1/estimate?enquiryNo=${orderNo}&status=DRAFT&status=SHARED&limit=1000&includeAudit=true`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async deleteDeliveryOptionInEstimateSplit(
    estimateNo: string,
    splitId: string,
    optionId: string,
  ) {
    const response = await axios({
      method: globalConstant.DELETE,
      url: endpoints.deleteDeliveryOptionInEstimateSplit(
        estimateNo,
        splitId,
        optionId,
      ),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async getOrderHandlers() {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getOrderHandlers,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async getAllOrderHandlers() {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getAllOrderHandlers,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async getInsuranceUsers(userSubType: "SPOC" | "SURVEYOR") {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getInsuranceUsers(userSubType),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async getDistinctCustomerStates() {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getDistinctCustomerStates,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async updateOrder(orderId: string, payload: any) {
    try {
      const response = await axios({
        method: globalConstant.PATCH,
        url: endpoints.updateOrDeleteOrder(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data: payload,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateOrderMetaInfo(orderId: string, payload: any) {
    try {
      const response = await axios({
        method: globalConstant.PATCH,
        url: endpoints.updateOrderMetaInfo(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data: payload,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateOrderPhantomFeedback(data: any) {
    try {
      const response = await axios({
        method: globalConstant.POST,
        url: endpoints.upsertPhantomFeedback,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data,
      });
      return response.data;
    } catch (error) {}
  }

  async checkNextStateOfGaraazOrders(orderId: string, action: string) {
    const response = await axios({
      method: globalConstant.POST,
      url: `${endpoints.updateOrDeleteOrder(
        orderId,
      )}/garaaz_transition?action=${action}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async cancelGaraazOrder(orderId: string, payload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.cancelGaraazOrder(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async allocateSupplierOrder(orderId: string, payload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.allocateSupplierGaraazOrder(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }
  async garaazOrderCBO(orderId: string, payload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.garaazOrderCBO(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async splitOrder(
    orderNo: string,
    orderId: string,
    newOrderItems: any,
    newOrderData: any,
  ) {
    const response1 = axios({
      method: globalConstant.POST,
      url: endpoints.placeGaraazOrder,
      data: newOrderData,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    const response2 = axios({
      method: globalConstant.PATCH,
      url: endpoints.updateOrDeleteOrder(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: newOrderItems,
    });

    const currentLoggedInUser = JSON.parse(
      localStorage.getItem("ORCA_USER") ?? "{}",
    );

    const response3 = await axios({
      method: globalConstant.PATCH,
      url: endpoints.setOrderRemarksByOrderNo((await response1).data.orderNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        userName: `${currentLoggedInUser.firstName} ${currentLoggedInUser.lastName}`,
        remarks: `Order created from Order No : ${orderNo}`,
      },
    });
    return response3.data;
  }

  async mergeOrder(mainOrderNo: string, dupicateOrderId: string, payload: any) {
    const response1 = await axios({
      method: globalConstant.PATCH,
      url: endpoints.mergeOrder(mainOrderNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    const response2 = await axios({
      method: globalConstant.DELETE,
      url: endpoints.updateOrDeleteOrder(dupicateOrderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        deletionReason: "Order merged with another order",
      },
    });
  }

  async deleteOrder(orderNo: string, deletionReason: string) {
    const response = await axios({
      method: globalConstant.DELETE,
      url: endpoints.deleteOrder(orderNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: { deletionReason },
    });
    return response.data;
  }

  async searchPartWithPartNumber(partNumber: string) {
    try {
      const response = await axios({
        method: "GET",
        url: endpoints.getSearchedPartList(partNumber),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }
  async insertOrUpdatePart(
    orderId: string,
    part: { partId: string; orderedQuantity: number },
  ) {
    try {
      const response = await axios({
        method: "PATCH",
        url: endpoints.insertOrUpdateGaraazOrderItemsPart(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data: part,
      });
      return response.data;
    } catch (error) {
      throw new Error("");
    }
  }
  async deletePart(
    orderId: string,
    part: { partId: string; orderedQuantity: number },
  ) {
    try {
      const response = await axios({
        method: "DELETE",
        url: endpoints.deleteGaraazOrderPart(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
        data: part,
      });
      return response.data;
    } catch (error) {
      throw new Error("");
    }
  }

  async getAllSuppliers() {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getAllSuppliersV2,
    });
    return response.data;
  }

  async getBrandsInfo() {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getBrands,
    });
    return response.data;
  }

  async getMediaData(mediaId: string) {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getMediaById(mediaId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {
      throw new Error();
    }
  }

  async addMedia(payload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.addMedia,
      data: payload,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }
  async getActiveSubscriptionCustomers() {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getActiveSubscriptionCustomers,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    return response.data.data;
  }

  async throwETAMissedEvent(
    orderId: any,
    etaGivenTime: string,
    etaTime: string,
  ) {
    try {
      const response = await axios({
        method: globalConstant.POST,
        data: {
          etaGivenTime: etaGivenTime,
          etaTime: etaTime,
        },
        url: endpoints.missedEta(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async throwACKMissedEvent(orderId: any) {
    try {
      const response = await axios({
        method: globalConstant.POST,
        url: endpoints.missedAck(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async throwETAProvideMissedEvent(orderId: any) {
    try {
      const response = await axios({
        method: globalConstant.POST,
        url: endpoints.missedETAProvide(orderId),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async getReturnedOrderItems(orderId: any) {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: `${endpoints.getAllGaraazOrders}&parentOrderId=${orderId}&orderType=RETURN&includeTestOrders=true`,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });

      return response.data.orders;
    } catch (error) {}
  }
  async getCBOOrders(orderId: any) {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: `${endpoints.getAllGaraazOrders}&parentOrderId=${orderId}&orderType=REGULAR&includeTestOrders=true`,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });

      return response.data.orders;
    } catch (error) {}
  }
  async getSingleOrder(orderId: any) {
    try {
      if (orderId) {
        const response = await axios({
          method: globalConstant.GET,
          url: endpoints.getGaraazOrder(orderId),
          headers: {
            Authorization: localStorage.getItem("auth_orca"),
          },
        });
        return response.data;
      }

      return {};
    } catch (error) {}
  }
  async getOrderStatsByOrderHandler() {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getOrderStatsByOrderHandler,
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {}
  }

  async fetchModels(carMaker: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `${endpoints.fetchModels}?maker=${carMaker}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async fetchMakers() {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.fetchMakers,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async fetchGenerations(carModel: string, carMaker: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `${endpoints.fetchGenerations}?model=${carModel}&maker=${carMaker}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async fetchCars(carGeneration: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `${endpoints.getCars}?generation=${carGeneration}`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async getCustomerAllSubscriptions(customerId: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getCustomerAllSubscriptions(customerId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }
  async getUniqueCustomers(range: any) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getUniqueCustomer + range,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async receivePayment(payload: CollectPaymentPayload) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.receivePayment,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: payload,
    });
    return response.data;
  }

  async updateReturnAmount(orderId: string, returnAmount: number) {
    const response = await axios({
      method: globalConstant.PATCH,
      url: endpoints.updateOrDeleteOrder(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        returnAmount: returnAmount,
      },
    });
    return response.data;
  }

  async getChildOrders(orderId: string, filterString: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getChildOrders(orderId) + filterString,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async createVirtualOrder(orderData: any) {
    const virtualOrderData = {
      ...orderData,
      orderType: OrderTypes.VIRTUAL,
      orderItems: [],
      allocatedTo: undefined,
      transactionType: undefined,
      rootOrderId: undefined,
      descendantOrderIds: [orderData._id],
    };

    const response = await this.placeGaraazOrder(virtualOrderData);
    return response;
  }

  async createRemainingOrder(
    orderData: any,
    remainingOrderItems: any[],
    rootOrderId: string,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    parentOrderETA?: any,
  ) {
    const remainingOrderData = {
      ...orderData,
      orderItems: remainingOrderItems,
      allocatedTo: undefined,
      transactionType: transactionType,
      rootOrderId,
      parentOrderId: orderData._id,
    };

    const response = await this.placeGaraazOrder(
      remainingOrderData,
      "orderHandlerAllocationType=" + OrderHandlerAllocationType.PARENT,
    );

    const currentLoggedInUser = JSON.parse(
      localStorage.getItem("ORCA_USER") ?? "{}",
    );

    let updatedRemainingOrder: any = await axios({
      method: globalConstant.PATCH,
      url: endpoints.setOrderRemarksByOrderNo(response.orderNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        userName: `${currentLoggedInUser.firstName} ${currentLoggedInUser.lastName}`,
        remarks: `Order created from Order No : ${orderData.orderNo}`,
      },
    });

    const updateOrderPayload: {
      PPDAmount: any;
      orderETA: any;
      orderDiscount?: string;
      creditNotePercentage?: string;
      cptSlabLevel?: number;
    } = {
      PPDAmount: orderData.PPDAmount,
      orderETA: parentOrderETA,
      cptSlabLevel: -1,
    };

    if (transactionType === TransactionType.TRADING) {
      updateOrderPayload.orderDiscount = String(discount);
      updateOrderPayload.creditNotePercentage = String(creditNotePercentage);
    }

    updatedRemainingOrder = await this.updateOrder(
      updatedRemainingOrder?.data?._id,
      updateOrderPayload,
    );
    try {
      const ackOrder = await this.checkNextStateOfGaraazOrders(
        updatedRemainingOrder?._id,
        OrderStatus.ACK,
      );

      const updatedMetaInfoOrder = await this.updateOrderMetaInfo(
        ackOrder?.order?._id,
        orderData.metaInfo,
      );
      return updatedMetaInfoOrder;
    } catch (error) {
      console.error("Failed to auto-ACK remaining order:", error);
      return updatedRemainingOrder.data;
    }
  }

  async updateVirtualOrderDescendants(
    virtualOrderId: string,
    newOrderId: string,
  ) {
    const response = await axios({
      method: globalConstant.PATCH,
      url: endpoints.updateOrDeleteOrder(virtualOrderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        descendantOrderIds: [newOrderId],
      },
    });
    return response.data;
  }

  async assignSupplierWithPartialFulfillment(
    orderId: string,
    supplierId: string,
    fulfilledOrderItems: any[],
    remainingOrderItems: any[],
    orderData: any,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    cptSlabDto?: any,
    orderETA?: any,
    parentOrderETA?: any,
  ) {
    try {
      const allocationPayload: any = {
        supplierId,
        ...(cptSlabDto && { cptSlab: cptSlabDto }),
      };

      if (orderETA && orderETA.length > 0) {
        allocationPayload.orderETA = orderETA;
      }

      const allocationResponse = await this.allocateSupplierOrder(
        orderId,
        allocationPayload,
      );

      const hasRootOrderId =
        orderData.rootOrderId && orderData.rootOrderId !== orderData._id;

      const newOrders: any = {};
      let virtualOrder: any;

      if (!hasRootOrderId) {
        virtualOrder = await this.createVirtualOrder(orderData);
        newOrders.virtualOrderNo = virtualOrder.orderNo;

        if (remainingOrderItems.length > 0) {
          const remainingOrder = await this.createRemainingOrder(
            orderData,
            remainingOrderItems,
            virtualOrder._id,
            transactionType,
            creditNotePercentage,
            discount,
            parentOrderETA,
          );

          newOrders.remainingOrderNo = remainingOrder.orderNo;
          newOrders.remainingOrder = remainingOrder;
          await this.updateOrder(orderId, {
            orderItems: fulfilledOrderItems,
            parentOrderId: virtualOrder?._id,
            rootOrderId: virtualOrder?._id,
          });
          await this.updateOrder(virtualOrder._id, {
            descendantOrderIds: [remainingOrder._id],
          });
        }
      } else {
        if (remainingOrderItems.length > 0) {
          const remainingOrder = await this.createRemainingOrder(
            orderData,
            remainingOrderItems,
            orderData.rootOrderId,
            transactionType,
            creditNotePercentage,
            discount,
            parentOrderETA,
          );

          newOrders.remainingOrderNo = remainingOrder.orderNo;
          newOrders.remainingOrder = remainingOrder;
          await this.updateOrder(orderData.rootOrderId, {
            descendantOrderIds: [remainingOrder._id],
          });
        }
        await this.updateOrder(orderId, { orderItems: fulfilledOrderItems });
      }

      return {
        allocationResponse,
        newOrders,
      };
    } catch (error) {
      throw error;
    }
  }

  async createReturnOrder(
    orderNo: string,
    returnItems: Array<{ partId: string; returnQuantity: number }>,
    returnReason: string,
  ) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.createReturnOrder(orderNo),
      data: {
        returnItems,
        returnReason,
      },
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async getVehicleByLPN(licensePlateNumber: string) {
    try {
      const response = await axios({
        method: globalConstant.GET,
        url: endpoints.getVehicleByLPN(licensePlateNumber),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateVehicleByLPN(licensePlateNumber: string, vehicleData: any) {
    try {
      const response = await axios({
        method: globalConstant.PATCH,
        url: endpoints.updateVehicleByLPN(licensePlateNumber),
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
          "Content-Type": "application/json",
        },
        data: vehicleData,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getGrazeons(query?: { userSubType?: string }) {
    const params = new URLSearchParams();
    if (query?.userSubType) {
      params.append("userSubType", query.userSubType);
    }

    const response = await axios({
      method: globalConstant.GET,
      url: `${endpoints.getGrazeons}${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async fetchDeliveryOptions(orderNo: string, payload: any) {
    const response = await axios({
      method: globalConstant.POST,
      url: `${endpoints.fetchDeliveryOptions(orderNo)}`,
      data: payload,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async createManualDeliveryOptionForEstimateSplit(
    estimateNo: string,
    splitId: string,
    payload: CreateManualDeliveryOptionPayload,
  ): Promise<ManualDeliveryOptionResponse> {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.createManualDeliveryOption(estimateNo, splitId),
      data: payload,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async confirmEstimateSplit(
    estimateNo: string,
    splitId: string,
    payload: ConfirmEstimateSplitPayload,
    mode: EstimateSplitMode = "assign",
  ) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.confirmEstimateSplit(estimateNo, splitId, mode),
      data: payload,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async cancelEstimateSplit(
    estimateNo: string,
    splitId: string,
    mode: EstimateSplitMode = "assign",
  ) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.cancelEstimateSplit(estimateNo, splitId, mode),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async holdEstimateSplit(
    estimateNo: string,
    splitId: string,
    mode: EstimateSplitMode = "assign",
  ) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.holdEstimateSplit(estimateNo, splitId, mode),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async shareEstimate(estimateNo: string) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.shareEstimate(estimateNo),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async refreshOutstanding(customerId: string): Promise<any> {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.refreshOutstanding(customerId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async getSupplierPartAvailability(requestId: string, partIds?: string[]) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getSupplierPartAvailability(requestId, partIds),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async orderItemsAction(
    orderNo: string,
    action: OrderItemActionType,
    items: Array<{ partId: string; quantity: number }>,
    mode?: EstimateSplitMode,
  ): Promise<OrderItemActionResponse> {
    const response = await axios({
      method: globalConstant.PATCH,
      url: endpoints.orderItemsAction(orderNo, mode),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: { action, items },
    });
    return response.data;
  }

  /**
   * Void an invoice (order or delivery type).
   * @param orderNo - The order number
   * @param invoiceNumber - The invoice number to void
   * @param invoiceType - Explicit type: 'ORDER' or 'DELIVERY'
   * @param reason - Reason for voiding the invoice
   * @returns Response from the void operation
   */
  async voidInvoice(
    orderNo: string,
    invoiceNumber: string,
    invoiceType: "ORDER" | "DELIVERY",
    reason: string,
  ) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.voidOrderInvoice(orderNo, invoiceNumber),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        invoiceType,
        reason,
      },
    });
    return response.data;
  }

  async generateConsolidatedInvoice(orderId: string) {
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.generateConsolidatedInvoice(orderId),
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  }

  async regenerateOrderInvoice(
    orderId: string,
    eventName: EventBusEventNames = EventBusEventNames.REGENERATE_ORDER_INVOICE,
  ) {
    const currentUser = JSON.parse(
      localStorage.getItem(globalConstant.ORCA_USER) ?? "{}",
    );
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.eventBus,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        type: EventBusTypes.ORCA_ORDER_ACTION_EVENT,
        name: eventName,
        orderId: orderId,
        userId: currentUser._id,
      },
    });
    return response.data;
  }

  async regenerateDeliveryInvoice(orderId: string, deliveryCost: number) {
    const currentUser = JSON.parse(
      localStorage.getItem(globalConstant.ORCA_USER) ?? "{}",
    );
    const response = await axios({
      method: globalConstant.POST,
      url: endpoints.eventBus,
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
      data: {
        type: EventBusTypes.ORCA_ORDER_ACTION_EVENT,
        name: EventBusEventNames.REGENERATE_DELIVERY_INVOICE,
        orderId: orderId,
        userId: currentUser._id,
        data: {
          deliveryCost: deliveryCost,
        },
      },
    });
    return response.data;
  }

  async getSupplierInventoryDeltas(rootPartNumbers: string[]) {
    const BATCH_SIZE = 50;
    const uniqueRootPartNumbers = Array.from(
      new Set(
        rootPartNumbers
          .map((rootPartNumber) => rootPartNumber?.trim())
          .filter(Boolean),
      ),
    );

    const rootPartNumberBatches: string[][] = [];
    for (
      let startIndex = 0;
      startIndex < uniqueRootPartNumbers.length;
      startIndex += BATCH_SIZE
    ) {
      rootPartNumberBatches.push(
        uniqueRootPartNumbers.slice(startIndex, startIndex + BATCH_SIZE),
      );
    }

    const supplierInventoryDeltaResponses = await Promise.allSettled(
      rootPartNumberBatches.map((rootPartNumberBatch) =>
        axios({
          method: globalConstant.GET,
          url: endpoints.getSupplierInventoryDeltas(rootPartNumberBatch),
          headers: {
            Authorization: localStorage.getItem("auth_orca"),
          },
        }),
      ),
    );

    return {
      results: supplierInventoryDeltaResponses.flatMap((response) =>
        response.status === "fulfilled"
          ? (response.value.data?.results ?? [])
          : [],
      ),
    };
  }
}

export default new Services();
